import { useEffect, useRef, useState } from 'react';

import { MFAMethod } from '../types';

const SEND_COOLDOWN_MS = 30 * 1000;

export function useMFACooldowns() {
  const [cooldowns, setCooldowns] = useState({ email: 0, sms: 0, rateLimit: 0 });
  const cooldownDeadlines = useRef(cooldowns);
  const secondsRemaining = (deadline: number) => Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  const methodCooldownSeconds = {
    email: secondsRemaining(Math.max(cooldowns.email, cooldowns.rateLimit)),
    sms: secondsRemaining(Math.max(cooldowns.sms, cooldowns.rateLimit)),
  };
  const restrictSending = (method: MFAMethod | 'rateLimit') => {
    const next = { ...cooldownDeadlines.current, [method]: Date.now() + SEND_COOLDOWN_MS };

    cooldownDeadlines.current = next;
    setCooldowns(next);
  };

  const reset = () => {
    cooldownDeadlines.current = { email: 0, sms: 0, rateLimit: 0 };
    setCooldowns(cooldownDeadlines.current);
  };

  useEffect(() => {
    const deadlines = Object.values(cooldowns).filter((deadline) => deadline > 0);

    if (!deadlines.length) {
      return undefined;
    }
    // Wake only when availability changes, never on a per-second interval.
    const timer = setTimeout(() => {
      const current = cooldownDeadlines.current;
      const now = Date.now();
      const next = {
        email: current.email > now ? current.email : 0,
        sms: current.sms > now ? current.sms : 0,
        rateLimit: current.rateLimit > now ? current.rateLimit : 0,
      };

      cooldownDeadlines.current = next;
      setCooldowns(next);
    }, Math.max(0, Math.min(...deadlines) - Date.now()));

    return () => clearTimeout(timer);
  }, [cooldowns]);

  return {
    canSend: (method: MFAMethod) =>
      Date.now() >= Math.max(cooldownDeadlines.current[method], cooldownDeadlines.current.rateLimit),
    recordSend: (method: MFAMethod) => restrictSending(method),
    recordRateLimit: () => restrictSending('rateLimit'),
    reset,
    methodCooldownSeconds,
    rateLimitCooldownSeconds: secondsRemaining(cooldowns.rateLimit),
  };
}
